import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Download,
  Users,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Star,
  FileText,
  Receipt,
  Shield,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const PAGE_SIZE = 20;

const PLAN_STYLES = {
  free: { label: 'Free', bg: 'bg-white/[0.06]', text: 'text-white/50', border: 'border-white/[0.06]' },
  single_download: { label: 'Single Download', bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  get_paid_pack: { label: 'Get Paid Pack', bg: 'bg-[#FF4500]/10', text: 'text-[#FF4500]', border: 'border-[#FF4500]/20' },
  pro: { label: 'Pro', bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
};

function PlanBadge({ plan }) {
  const style = PLAN_STYLES[plan] || PLAN_STYLES.free;
  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text} border ${style.border}`} data-testid={`plan-badge-${plan}`}>
      {style.label}
    </span>
  );
}

function formatDate(isoString) {
  if (!isoString) return '-';
  try {
    const d = new Date(isoString);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  } catch {
    return '-';
  }
}

export default function AdminUsersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [expandedReview, setExpandedReview] = useState(null);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setUsers(res.data.users || []);
    } catch (err) {
      if (err.response?.status === 403) {
        navigate('/dashboard');
      } else {
        const detail = err.response?.data?.detail || err.message || 'Failed to load users';
        console.error('Admin users error:', err.response?.status, detail);
        toast.error(typeof detail === 'string' ? detail : 'Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let list = users;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (u) => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a, b) => {
      let aVal, bVal;
      if (sortKey === 'created_at') {
        aVal = a.created_at || '';
        bVal = b.created_at || '';
      } else if (sortKey === 'plan') {
        const order = { pro: 4, get_paid_pack: 3, single_download: 2, free: 1 };
        aVal = order[a.plan] || 0;
        bVal = order[b.plan] || 0;
      } else if (sortKey === 'invoices') {
        aVal = a.invoice_count;
        bVal = b.invoice_count;
      } else if (sortKey === 'letters') {
        aVal = a.letter_count;
        bVal = b.letter_count;
      } else {
        aVal = a[sortKey] || '';
        bVal = b[sortKey] || '';
      }
      if (sortDir === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
    return list;
  }, [users, search, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
    setPage(1);
  };

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Date Joined', 'Plan Type', 'Invoices Created', 'Emails Generated'];
    const rows = users.map((u) => [
      u.name || '', u.email || '', formatDate(u.created_at),
      PLAN_STYLES[u.plan]?.label || u.plan, u.invoice_count, u.letter_count,
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const today = new Date().toISOString().split('T')[0];
    a.href = url;
    a.download = `users-export-${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };


  const SortHeader = ({ label, sortKeyName, className = '' }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold text-white/40 uppercase tracking-wider cursor-pointer hover:text-white/70 transition-colors select-none ${className}`}
      onClick={() => handleSort(sortKeyName)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${sortKey === sortKeyName ? 'text-[#FF4500]' : ''}`} />
      </span>
    </th>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050507] pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#FF4500] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050507] pt-24 pb-16 px-4" data-testid="admin-users-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF4500]">Admin</span>
            <h1 className="font-clash text-3xl sm:text-4xl font-medium text-white mt-1">User Database</h1>
            <div className="flex items-center gap-2 mt-2">
              <Users className="w-4 h-4 text-white/40" />
              <span className="text-sm text-white/50" data-testid="total-users-count">Total Users: {users.length}</span>
            </div>
          </div>
          <Button
            onClick={exportCSV}
            className="bg-[#FF4500] hover:bg-[#CC3700] text-white gap-2 h-10 px-5 rounded-xl"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/25 focus:border-[#FF4500]/40 h-10"
              data-testid="user-search-input"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-[#0c0c10] border border-white/[0.06] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <SortHeader label="Name" sortKeyName="name" />
                  <SortHeader label="Email" sortKeyName="email" />
                  <SortHeader label="Date Joined" sortKeyName="created_at" />
                  <SortHeader label="Plan" sortKeyName="plan" />
                  <SortHeader label="Invoices" sortKeyName="invoices" />
                  <SortHeader label="Emails" sortKeyName="letters" />
                  <th className="px-4 py-3 text-left text-xs font-semibold text-white/40 uppercase tracking-wider">Reviews</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-white/30 text-sm">
                      {search ? 'No users match your search' : 'No users found'}
                    </td>
                  </tr>
                ) : (
                  paginated.map((u, idx) => (
                    <React.Fragment key={u.user_id || idx}>
                      <tr
                        className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors"
                        data-testid={`user-row-${idx}`}
                      >
                        <td className="px-4 py-3.5">
                          <span className="text-sm text-white font-medium">{u.name || '-'}</span>
                          {u.role === 'admin' && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider text-[#FF4500] font-bold bg-[#FF4500]/10 px-1.5 py-0.5 rounded">Admin</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-white/60">{u.email}</td>
                        <td className="px-4 py-3.5 text-sm text-white/50">{formatDate(u.created_at)}</td>
                        <td className="px-4 py-3.5"><PlanBadge plan={u.plan} /></td>
                        <td className="px-4 py-3.5">
                          <span className="inline-flex items-center gap-1.5 text-sm text-white/60">
                            <Receipt className="w-3.5 h-3.5 text-white/30" />
                            {u.invoice_count}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="inline-flex items-center gap-1.5 text-sm text-white/60">
                            <FileText className="w-3.5 h-3.5 text-white/30" />
                            {u.letter_count}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          {u.review_count > 0 ? (
                            <button
                              onClick={() => setExpandedReview(expandedReview === u.user_id ? null : u.user_id)}
                              className="inline-flex items-center gap-1.5 text-sm text-amber-400 hover:text-amber-300 transition-colors"
                              data-testid={`review-toggle-${idx}`}
                            >
                              <Star className="w-3.5 h-3.5 fill-amber-400" />
                              {u.review_count}
                            </button>
                          ) : (
                            <span className="text-sm text-white/25">-</span>
                          )}
                        </td>
                      </tr>
                      {expandedReview === u.user_id && u.reviews?.length > 0 && (
                        <tr>
                          <td colSpan={7} className="px-4 py-3 bg-white/[0.01]">
                            <div className="space-y-2 max-w-2xl" data-testid={`review-detail-${idx}`}>
                              {u.reviews.map((r, ri) => (
                                <div key={ri} className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                                  <div className="flex gap-0.5 shrink-0 mt-0.5">
                                    {[1, 2, 3, 4, 5].map((s) => (
                                      <Star key={s} className={`w-3 h-3 ${s <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-white/10'}`} />
                                    ))}
                                  </div>
                                  <p className="text-sm text-white/60 leading-relaxed">{r.comment || <span className="italic text-white/25">No comment</span>}</p>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
              <span className="text-xs text-white/40">
                Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="text-white/40 hover:text-white/70 h-8 w-8 p-0"
                  data-testid="pagination-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs text-white/50 px-2">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="text-white/40 hover:text-white/70 h-8 w-8 p-0"
                  data-testid="pagination-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
